<template>
  <div class="track-detail">
    <!-- 顶部标题栏 -->
    <div class="header">
      <span class="back-btn" @click="$emit('back')">← 返回</span>
      <span class="title">物流详情</span>
      <span
        class="refresh-btn"
        :class="{ spinning: refreshing }"
        @click="refresh"
      >⟳</span>
    </div>

    <!-- 快递信息卡片 -->
    <div class="info-card" v-if="trackInfo">
      <div class="info-row">
        <span class="label">快递单号：</span>
        <span class="value">{{ trackInfo.carryBillNo }}</span>
        <span class="copy-btn" @click="copyBillNo">📋</span>
      </div>
      <div class="info-row">
        <span class="label">快递公司：</span>
        <span class="value">{{ trackInfo.logComName }}</span>
      </div>
      <div class="info-row">
        <span class="label">出库单号：</span>
        <span class="value">{{ trackInfo.billNo }}</span>
      </div>
      <div class="info-row" v-if="trackInfo.applyDate">
        <span class="label">申请日期：</span>
        <span class="value">{{ formatDate(trackInfo.applyDate) }}</span>
      </div>
      <div class="info-row" v-if="trackInfo.receiverName">
        <span class="label">收件人：</span>
        <span class="value">{{ trackInfo.receiverName }}</span>
      </div>
      <div class="info-row" v-if="trackInfo.receiverPhone">
        <span class="label">收件人电话：</span>
        <span class="value">{{ trackInfo.receiverPhone }}</span>
      </div>
      <div class="info-row address-row" v-if="trackInfo.receiverAddress">
        <span class="label">收件人地址：</span>
        <span class="value">{{ trackInfo.receiverAddress }}</span>
      </div>
    </div>

    <!-- 加载中 -->
    <div class="loading" v-if="loading">
      <div class="loading-spinner"></div>
      <div>加载中...</div>
    </div>

    <!-- 轨迹时间线 -->
    <div class="timeline" v-else-if="tracks.length > 0">
      <div class="track-header">
        <span class="track-title">物流轨迹</span>
        <span class="track-time" v-if="trackTime">数据更新于 {{ formatTime(trackTime) }}</span>
      </div>
      <div
        v-for="(track, index) in tracks"
        :key="index"
        class="timeline-item"
        :class="{ active: index === 0 }"
      >
        <div class="timeline-dot"></div>
        <div class="timeline-content">
          <div class="time">{{ track.time }}</div>
          <div class="context">{{ track.context }}</div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div class="empty" v-else>
      <div class="empty-icon">📦</div>
      <div class="empty-text">暂无物流信息</div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { apiUrl } from '../api/config'

export default {
  name: 'MobileTrackDetail',
  props: {
    trackInfo: {
      type: Object,
      required: true
    }
  },
  emits: ['back'],
  setup(props) {
    const loading = ref(false)
    const refreshing = ref(false)
    const tracks = ref([])
    const trackTime = ref(null)

    // 获取认证头
    const getAuthHeaders = () => {
      const token = localStorage.getItem('token')
      const headers = {
        'Accept': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      return headers
    }

    // 加载轨迹数据（force=true 时绕过缓存实时查询）
    const loadTracks = async (force = false) => {
      if (force) {
        refreshing.value = true
      } else {
        loading.value = true
      }
      try {
        const url = apiUrl(`/Api/Express/TrackDetail?carryBillNo=${encodeURIComponent(props.trackInfo.carryBillNo)}&force=${force}`)
        const res = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders()
        })

        if (res.status === 401) {
          localStorage.removeItem('token')
          localStorage.removeItem('userName')
          localStorage.removeItem('expireAt')
          localStorage.removeItem('isAdmin')
          window.location.reload()
          return
        }

        if (!res.ok) {
          throw new Error('查询失败')
        }

        const json = await res.json()
        tracks.value = json.data?.tracks || []
        trackTime.value = json.data?.trackTime || null
      } catch (e) {
        ElMessage.error(e?.message || '查询失败')
      } finally {
        loading.value = false
        refreshing.value = false
      }
    }

    // 手动刷新（强制实时查询）
    const refresh = () => {
      if (refreshing.value || loading.value) return
      loadTracks(true)
    }

    // 复制单号
    const copyBillNo = () => {
      navigator.clipboard.writeText(props.trackInfo.carryBillNo).then(() => {
        ElMessage.success('已复制')
      }).catch(() => {
        ElMessage.error('复制失败')
      })
    }

    // 格式化日期（yyyy-MM-dd）
    const formatDate = (value) => {
      if (!value) return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }

    // 格式化时间（MM-dd HH:mm）
    const formatTime = (value) => {
      if (!value) return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mm = String(date.getMinutes()).padStart(2, '0')
      return `${m}-${d} ${hh}:${mm}`
    }

    onMounted(() => {
      loadTracks()
    })

    return {
      loading,
      refreshing,
      tracks,
      trackTime,
      refresh,
      copyBillNo,
      formatDate,
      formatTime
    }
  }
}
</script>

<style scoped>
.track-detail {
  min-height: 100vh;
  background: #f5f5f5;
}

.header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #eee;
}

.back-btn {
  font-size: 16px;
  color: #1890ff;
  margin-right: 12px;
  cursor: pointer;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.refresh-btn {
  margin-left: auto;
  font-size: 20px;
  color: #1890ff;
  cursor: pointer;
  user-select: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.refresh-btn.spinning {
  animation: spin 1s linear infinite;
  pointer-events: none;
}

.info-row.address-row {
  align-items: flex-start;
}

.info-card {
  margin: 12px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.label {
  color: #999;
  min-width: 80px;
}

.value {
  color: #333;
  flex: 1;
}

.copy-btn {
  font-size: 14px;
  cursor: pointer;
  margin-left: 8px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #999;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #eee;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.timeline {
  margin: 12px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.track-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f5f5f5;
}

.track-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.track-time {
  font-size: 11px;
  color: #999;
}

.timeline-item {
  display: flex;
  padding-bottom: 20px;
  position: relative;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 14px;
  bottom: 0;
  width: 1px;
  background: #e8e8e8;
}

.timeline-item.active .timeline-dot {
  background: #1890ff;
}

.timeline-item.active .time {
  color: #1890ff;
}

.timeline-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #d9d9d9;
  margin-right: 12px;
  margin-top: 4px;
  flex-shrink: 0;
}

.timeline-content {
  flex: 1;
}

.time {
  font-size: 13px;
  color: #999;
  margin-bottom: 4px;
}

.context {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 14px;
  color: #999;
}
</style>
